"use client";
import { Stack, Text } from "@chakra-ui/react";
import { useTextVariants } from "@/theme/components/useSectionHeading";
import { SimpleList } from "@/components/list";

type ContentPointProps = {
  text: string;
  points: string[];
};

const ContentPoints = ({ text, points }: ContentPointProps) => {
  const textVariant = useTextVariants();
  return (
    <Stack spacing={4}>
      <Text variant={textVariant} color="text.100">
        {text}
      </Text>
      <SimpleList points={points} />
    </Stack>
  );
};

export const FAQS = [
  {
    title: "What are the main advantages of using a Hosted Grafana service?",
    content:
      "Hosted Grafana services offer simplified deployment, automatic scaling, enhanced security, and reduced operational overhead, allowing businesses to focus on visualizing and analyzing their data without managing the underlying infrastructure.",
  },
  {
    title: "Can I customize the configurations of my Hosted Grafana deployment?",
    content:
      "Yes, providers like Onemind offer customizable server configurations to meet specific performance and storage requirements, ensuring the solution aligns with your business needs.",
  },
  {
    title: "How does Hosted Grafana ensure data security?",
    content:
      "Data security is maintained through encryption at rest and in transit, strict access controls, and compliance with industry-standard security practices, safeguarding sensitive information.",
  },
  {
    title: "Is it possible to integrate Hosted Grafana with other cloud services?",
    content:
      "Absolutely. Hosted Grafana solutions are designed to integrate seamlessly with various cloud services, databases, and applications, providing a cohesive ecosystem for data management and analysis.",
  },
  {
    title: "What kind of support is available for Hosted Grafana services?",
    content:
      "Providers typically offer expert support for deployment, configuration, performance optimization, and ongoing maintenance, ensuring the solution operates efficiently and effectively.",
  },
  {
    title: "How does Hosted Grafana handle scalability during varying workloads?",
    content:
      "Hosted Grafana services offer elastic scalability, allowing resources to be adjusted dynamically based on data volumes and user loads, ensuring consistent performance even during peak periods.",
  },
  {
    title: "What is the difference between Hosted Grafana and self-managed Grafana?",
    content:
      "Hosted Grafana is a managed service where the provider handles infrastructure, maintenance, and scaling, reducing operational complexity. In contrast, self-managed Grafana requires in-house resources to manage all aspects of deployment and upkeep.",
  },
  {
    title: "Can Hosted Grafana support compliance with industry regulations?",
    content:
      "Yes, Hosted Grafana services are designed with security features and compliance certifications to help businesses meet industry-specific regulatory requirements.",
  },
  {
    title: "How does Onemind ensure the performance of Hosted Grafana deployments?",
    content:
      "Onemind utilizes high-performance bare-metal servers to provide low-latency, high-throughput data visualization. By leveraging dedicated resources, we ensure that businesses get a reliable and optimized Grafana environment with minimal downtime.",
  },
  {
    title:
      "What are the cost benefits of choosing Hosted Grafana over self-managed deployments?",
    content:
      "Hosted Grafana eliminates infrastructure costs, maintenance expenses, and DevOps overhead. With a pay-as-you-go model, businesses only pay for the resources they need, avoiding upfront capital expenditures (CAPEX) and reducing operational expenditures (OPEX). Additionally, hosted solutions provide built-in updates, security, and performance optimizations, reducing the hidden costs of self-managed deployments.",
  },
  {
    title: "Can Hosted Grafana handle large-scale enterprise monitoring? ",
    content: (
      <ContentPoints
        text="Yes! Hosted Grafana is designed for enterprise-grade monitoring and can handle massive data ingestion across multiple environments. Features that make it suitable for large-scale businesses include: "
        points={[
          "Distributed architecture for high availability. ",
          "multi-region and hybrid cloud support. ",
          " Seamless integration with data lakes and big data platforms.",
          "Real-time dashboard updates and alerting.",
        ]}
      />
    ),
  },
  {
    title: "How does Hosted Grafana improve real-time monitoring and alerting? ",
    content: (
      <ContentPoints
        text="Hosted Grafana provides real-time visualization and AI-driven alerting by: "
        points={[
          "Streaming data from multiple sources (Prometheus, Influx DB, Elasticsearch, AWS CloudWatch, etc.). ",
          "Enabling anomaly detection with machine learning models. ",
          "Configurable alerting rules that notify teams via email, Slack, PagerDuty, or webhook integrations. ",
        ]}
      />
    ),
  },
];
